/**
 * Newsletter Popup — newsletter-popup.js
 *
 * Handles: delay timer, cookie-based dismissal, ESC key,
 * overlay click, success auto-close.
 */

(function () {
  'use strict';

  class NewsletterPopup {
    constructor(el) {
      this.el = el;
      this.sectionId = el.dataset.sectionId;
      this.delay = (parseInt(el.dataset.delay, 10) || 3) * 1000;
      this.cookieDays = parseInt(el.dataset.cookieDays, 10) || 30;
      this.cookieName = 'np_dismissed_' + this.sectionId;
      this.sessionKey = 'np_session_' + this.sectionId;
      this.isCenter = el.dataset.position === 'center' || !el.dataset.position;
      // Design mode: Shopify theme editor iframe OR preview-theme tab
      this.designMode = !!(window.Shopify && window.Shopify.designMode) ||
        new URLSearchParams(window.location.search).has('preview_theme_id');

      // Check whether the form was just submitted successfully
      this.wasSuccessful = !!el.querySelector('[data-success]');

      this.init();
    }

    init() {
      // Popup disabled in theme settings — do nothing
      if (this.el.dataset.enabled === 'false') return;

      // Inside the Shopify theme editor: show immediately, never close or set cookies
      if (this.designMode) {
        this.show();
        return;
      }

      // On any page reload (F5 / Ctrl+R / Ctrl+Shift+R) reset popup state
      // so it appears fresh — this is intentional for both testing and real visitors
      const navType = ((performance.getEntriesByType('navigation')[0]) || {}).type;
      if (navType === 'reload') {
        this.clearCookie();
        try { sessionStorage.removeItem(this.sessionKey); } catch (e) {}
      }
      // Form was just submitted — show success, set cookie, auto-close
      if (this.wasSuccessful) {
        this.show();
        this.setCookie();
        this.setSession();
        setTimeout(() => this.hide(), 4000);
        return;
      }

      // Already dismissed or subscribed (long-term cookie) — don't show
      if (this.getCookie()) return;

      // Already shown this session (page navigation) — don't show again
      // sessionStorage clears on tab close and on hard refresh (Ctrl+F5)
      if (sessionStorage.getItem(this.sessionKey)) return;

      // Show after delay
      this._timer = setTimeout(() => this.show(), this.delay);

      // Wire up close triggers
      this.el.querySelectorAll('[data-close-popup]').forEach((trigger) => {
        trigger.addEventListener('click', () => this.dismiss());
      });

      // ESC key
      this._onKey = (e) => {
        if (e.key === 'Escape' && !this.el.hidden) this.dismiss();
      };
      document.addEventListener('keydown', this._onKey);

      // Phone → note: before submit, copy phone value into the hidden contact[note] input
      const form = this.el.querySelector('.newsletter-popup__form');
      const phoneInput = this.el.querySelector('.newsletter-popup__phone-input');
      const noteInput = this.el.querySelector('.newsletter-popup__note-input');
      if (form && phoneInput && noteInput) {
        form.addEventListener('submit', () => {
          const phone = phoneInput.value.trim();
          const prefix = noteInput.dataset.prefix || '';
          noteInput.value = phone ? prefix + phone : '';
        });
      }
    }

    show() {
      this.el.hidden = false;
      // Don't lock body scroll in the theme editor (breaks editor scrolling)
      if (this.isCenter && !this.designMode) document.body.style.overflow = 'hidden';
      // Mark as shown this session so navigating to other pages won't re-trigger
      this.setSession();
      // Trigger CSS transition on next frame
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.el.classList.add('newsletter-popup--visible');
        });
      });
      // Move focus into the popup for accessibility
      const firstInput = this.el.querySelector('input, button');
      if (firstInput) {
        setTimeout(() => firstInput.focus(), 350);
      }
    }

    hide() {
      this.el.classList.remove('newsletter-popup--visible');
      this.el.addEventListener(
        'transitionend',
        () => {
          this.el.hidden = true;
          if (this.isCenter) document.body.style.overflow = '';
        },
        { once: true }
      );
      if (this._onKey) document.removeEventListener('keydown', this._onKey);
    }

    dismiss() {
      this.setCookie();
      this.hide();
    }

    setCookie() {
      const d = new Date();
      d.setDate(d.getDate() + this.cookieDays);
      document.cookie =
        encodeURIComponent(this.cookieName) +
        '=1; expires=' +
        d.toUTCString() +
        '; path=/; SameSite=Lax';
    }

    clearCookie() {
      document.cookie =
        encodeURIComponent(this.cookieName) +
        '=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax';
    }

    getCookie() {
      const name = encodeURIComponent(this.cookieName) + '=';
      return document.cookie.split(';').some((c) => c.trim().startsWith(name));
    }

    setSession() {
      try {
        sessionStorage.setItem(this.sessionKey, '1');
      } catch (e) {
        // sessionStorage unavailable (private mode edge cases) — safe to ignore
      }
    }
  }

  // Boot after DOM is ready
  function init() {
    const popup = document.querySelector('.newsletter-popup[data-section-id]');
    if (popup) new NewsletterPopup(popup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
